import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import RegistryDrawer from "@/components/RegistryDrawer";
import HeaderSwitcher from "@/components/HeaderSwitcher";
import MyBuilds from "@/components/MyBuilds";
import DashboardWidgets from "@/components/DashboardWidgets";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Plus, BookOpen, Circle } from "lucide-react";
import { toast } from "sonner";
import { Glossary } from "@/components/kit";
import Greeting from "@/components/Greeting";
import LoadingButton from "@/components/LoadingButton";
import FieldError from "@/components/FieldError";
import OrientationTour from "@/components/OrientationTour";
import { ReferralCard } from "@/components/ReferralCard";

// PATCH 35 M3 — first-time user tour, tailored to the sector/region picked at signup.
function UserTour({ sector, region }) {
  const focus = [sector, region].filter(Boolean).join(" · ") || "your project";
  const steps = [
    { title: "Welcome — this dashboard is built around your idea", body: `We've set things up for ${focus}. Here's a 20-second tour of the three things you'll use most.` },
    { title: "Your Compliance Journey", body: "The timeline tracks your project from idea to evidence pack, in plain English — always showing what's next, never a black box." },
    { title: "The Coach & the catalogue", body: "The Coach explains its suggestions and quiets down when you've heard enough; the catalogue surfaces the real connectors and compliance considerations relevant to your market." },
  ];
  return <OrientationTour tourKey="user_v1" steps={steps} testid="user-orientation" />;
}

// PATCH 28 BW11 — one-time, dismissible honesty step: genuine current gaps, stated once and clearly.
function HonestyStep() {
  const KEY = "kadana_honesty_dismissed_v1";
  const [show, setShow] = useState(() => { try { return !localStorage.getItem(KEY); } catch { return true; } });
  if (!show) return null;
  const dismiss = () => { try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ } setShow(false); };
  return (
    <div className="k-container" data-testid="honesty-step" style={{ padding: 18, margin: "14px 0", borderLeft: "3px solid var(--accent-primary)" }}>
      <div className="k-heading" style={{ fontSize: 15, fontWeight: 700 }}>What Kadana will not do yet</div>
      <ul className="k-muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.7 }}>
        <li>No per-country legal templates outside the UK, EU and Kenya — elsewhere you get generic guidance only.</li>
        <li>Connector access classes are best-effort until a Kadana admin verifies them.</li>
        <li>No automated business-data migration from your old system yet.</li>
      </ul>
      <button className="k-btn k-btn-ghost" data-testid="honesty-dismiss" style={{ marginTop: 6 }} onClick={dismiss}>Got it</button>
    </div>
  );
}

export default function Workspace() {
  const { org, user } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [np, setNp] = useState({ name: "", description: "", ai_region: "Any", compliance_mode: "uk_eu_default" });
  const [nameTouched, setNameTouched] = useState(false);
  const [projects, setProjects] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/prefs").then(r => setPrefs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (org?.id) api.get(`/orgs/${org.id}/projects`).then(r => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [org]);

  const create = async () => {
    try {
      const { data } = await api.post("/projects", { ...np, org_id: org.id });
      setShowNew(false); toast.success("Project created");
      nav(`/workspace/project/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const topup = async () => {
    try { const { data } = await api.post(`/orgs/${org.id}/topup`, { credits: 1000 });
      toast.success(data.message || "Top-up complete"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Shell active="workspace">
      <div className="k-hero k-glow-host" style={{ padding: "22px 22px", marginBottom: 16 }}>
        <div className="k-glow k-glow--a" style={{ width: 360, height: 360, top: -120, left: -60 }} />
        <div className="k-glow k-glow--b" style={{ width: 300, height: 300, bottom: -160, right: -40 }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div className="k-h-accent">
            <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }}>Workspace</h1>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="k-btn k-btn-ghost" data-testid="open-registry" onClick={() => setDrawer(true)}><BookOpen size={15} /> Search & Discovery</button>
            <button className="k-btn k-btn-ghost" data-testid="topup-btn" onClick={topup}>Top up credits</button>
            <button className="k-btn k-btn-primary" data-testid="new-project-btn" onClick={() => setShowNew(true)}><Plus size={15} /> New project</button>
          </div>
        </div>
      </div>

      <UserTour sector={org?.sector} region={org?.jurisdiction} />

      {showNew && (
        <div className="k-container k-fadein" style={{ padding: 22, marginTop: 20 }} data-testid="new-project-form">
          <h3 className="k-heading" style={{ marginTop: 0 }}>New project</h3>
          <input className="k-control" data-testid="np-name" placeholder="Project name" value={np.name}
            onChange={e => setNp({ ...np, name: e.target.value })} onBlur={() => setNameTouched(true)} style={{ marginBottom: 4 }} />
          <div style={{ marginBottom: 10 }}><FieldError msg={nameTouched && !np.name.trim() ? "Give your project a name to continue." : ""} testid="np-name-error" /></div>
          <textarea className="k-control" data-testid="np-desc" placeholder="Describe what you want in plain English…"
            value={np.description} onChange={e => setNp({ ...np, description: e.target.value })} rows={3} style={{ marginBottom: 10 }} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <label className="k-muted" style={{ fontSize: 13 }}>AI region&nbsp;
              <select className="k-control" data-testid="np-region" style={{ width: "auto", display: "inline-block" }}
                value={np.ai_region} onChange={e => setNp({ ...np, ai_region: e.target.value })}>
                <option>Any</option><option>UK</option><option>EU</option>
              </select>
            </label>
            <label className="k-muted" style={{ fontSize: 13 }}>Compliance track&nbsp;
              <select className="k-control" data-testid="np-mode" style={{ width: "auto", display: "inline-block" }}
                value={np.compliance_mode} onChange={e => setNp({ ...np, compliance_mode: e.target.value })}>
                <option value="uk_eu_default">UK/EU (recommended)</option>
                <option value="custom_scope">Custom / opt out</option>
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <LoadingButton className="k-btn k-btn-primary" testid="np-create" onClick={create} disabled={!np.name.trim()}>Create</LoadingButton>
            <button className="k-btn k-btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
        </div>
      )}

      {org && <MyBuilds orgId={org.id} />}

      <DashboardWidgets
        storageKey={`kadana_dash_v1_${user?.id || "anon"}`}
        visibleCount={2}
        widgets={[
          { id: "preferences", label: "Explanation, language & scope", node: <HeaderSwitcher prefs={prefs} onChange={setPrefs} /> },
          { id: "honesty", label: "What Kadana will not do yet", node: <HonestyStep /> },
          { id: "referral", label: "Refer & earn", node: <ReferralCard context="dashboard" onUpgrade={() => nav("/billing")} /> },
        ]}
      />

      <RegistryDrawer open={drawer} onClose={() => setDrawer(false)} />
    </Shell>
  );
}
